<template>
  <div>

    <table-tools :actions="tableActions">
      <slot name="tools" />
    </table-tools>

    <v-data-table-server
      :items-per-page="itemsPerPage"
      :items="auditLogs"
      :headers="augmentedHeaders"
      @update:options="loadItems"
      :loading="loadingEntries"
      :items-length="totalItems"
      :sort-by="sortBy"
      :search="tableSearch"
    >

      <!-- If any slots were passed to the AuditLogTable, pass them along to the VDataTableServer -->
      <!-- https://stackoverflow.com/a/71678136/19018560 -->
      <template      
        v-for="(_, name) in $slots"
        #[name]="slotData"
      >
        <slot :name="name" v-bind="slotData || {}" />
      </template>

      <template v-slot:item.targetType="{ item }">
        {{ getEnumLabel(item.targetType, AuditLogTargetTypes, item.targetType) }}
      </template>

      <template v-slot:item.action="{ item }">
        {{ getEnumLabel(item.action, AuditLogActions, item.action) }}
      </template>

      <template v-slot:item.username="{ item }">

        <template v-if="canManageUsers && item.userID">
          <router-link :to="{ name: 'userDetail', params: { userID: item.userID } }">
            {{ item.username ?? '(unknown user)' }}
          </router-link>
        </template>

        <template v-if="!canManageUsers">
          {{ item.username ?? '(unknown user)' }}
        </template>

      </template>

      <template v-slot:item.timestamp="{ item }">
        <!-- can/should we convert this from UTC to the user's local time? -->
        {{ moment(item.timestamp).format('MM/DD/YYYY h:mm:ss a') }}
      </template>

      <!-- Remove the "All" option from the pagination tools -->
      <template v-slot:bottom>
        <v-data-table-footer :items-per-page-options="[ {value: 10, title: '10'}, {value: 25, title: '25'}, {value: 50, title: '50'}, {value: 100, title: '100'}]" />
      </template>

    </v-data-table-server>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// models
import AuditLog, { type FetchAuditLogsRequest, type FetchAuditLogsResponseRecord } from '../models/AuditLog'

// enums
import { AuditLogActions, AuditLogTargetTypes } from '../enums'

// misc
import getEnumLabel from '../utils/getEnumLabel'
import moment from 'moment'
import NotificationService from '../services/NotificationService'
import type { Can } from '../main'
import type { VDataTableServerHeader, VDataTableServerSortBy } from '../types/vuetify'

interface Props {
  headers?: any[] // TODO: string[]?
  showTargetType?: boolean
  showTargetId?: boolean
  showAction?: boolean
  // Filters to pass to the API
  organizationID?: number
  locationID?: number
  targetTypes: string[]
  targetIds?: null | number[]
  // pagination options
  itemsPerPage?: number
  /**
   * The transformer function is responsible for converting a raw AuditLog
   * entry into a data table item. In other words, if the headers prop includes
   * a particular key, then your transformer function should return an object
   * which also includes that key.
   */
  transformer?: (auditLog: FetchAuditLogsResponseRecord) => any
}

const props = withDefaults(defineProps<Props>(), {
  headers: () => [],
  showTargetType: false,
  showTargetId: false,
  showAction: true,
  targetIds: () => [],
  itemsPerPage: 50,
  transformer: (_auditLog: FetchAuditLogsResponseRecord) => {
    // always return a separate object! don't just decorate and return auditLog!
    return {}
  }
})

const $can = inject('$can') as Can
const canManageUsers = $can('users.index')
const canSeeIds = $can('seeids')
const { t } = useI18n()

/**
 * Table tools
 */

const tableActions = computed(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: () => tableSearch.value = Math.random().toString(),
    busy: loadingEntries.value,
  },
])

/**
 * Data table
 */

const sortBy = ref<VDataTableServerSortBy[]>([{ key: 'timestamp', order: 'desc' }])

// sort of a kludge. updating this value forces the table to reload data from the server. it doesn't actually have anything to do with search.
const tableSearch = ref<string>('')

const augmentedHeaders = computed<VDataTableServerHeader[]>(() => {

  // Before adding any columns, we first make sure that they aren't already
  // included in props.headers. This will allow the parent component to reorder
  // these columns, if desired.

  const output: VDataTableServerHeader[] = []

  if (!props.headers.some(h => h.key === 'ID') && canSeeIds) {
    output.push({key: 'ID', title: t('titles.log_id'), sortable: false})
  }

  if (!props.headers.some(h => h.key === 'targetType') && props.showTargetType) {
    output.push({key: 'targetType', title: t('titles.target_type')})
  }

  if (!props.headers.some(h => h.key === 'targetID') && props.showTargetId && canSeeIds) {
    output.push({key: 'targetID', title: t('titles.target_id')})
  }

  if (!props.headers.some(h => h.key === 'action') && props.showAction) {
    output.push({key: 'action', title: t('titles.action')})
  }

  output.push(...props.headers)

  if (!props.headers.some(h => h.key === 'username')) {
    output.push({key: 'username', title: t('titles.user'), sortable: false})
  }

  if (!props.headers.some(h => h.key === 'timestamp')) {
    output.push({key: 'timestamp', title: t('titles.date_time_utc')})
  }

  return output

})

const auditLogs = ref<FetchAuditLogsResponseRecord[]>([])
const totalItems = ref<number>(0)

const loadingEntries = ref<boolean>(false)

// reload log entries when relevant props change
watch(
  [
    () => props.organizationID,
    () => props.locationID,
    () => props.targetTypes,
    () => props.targetIds,
  ],
  () => {
    // force a reload
    tableSearch.value = Math.random().toString()
  }
)

function loadItems({ page, itemsPerPage, sortBy }: any) {

  loadingEntries.value = true

  let sortKey: string = 'event_dt_utc'
  let sortOrder: string = 'desc'

  if (sortBy.length) {

    // AuditLog.fetch() uses different casing (and even different names) for some fields in the request vs. the
    // response. That's not cool. Convert as appropriate.

    const map: {[key: string]: string} = {
      timestamp: 'event_dt_utc',
      targetType: 'target_type',
      targetID: 'target_id',
    }

    sortKey = map[sortBy[0].key] ?? sortBy[0].key
    sortOrder = sortBy[0].order

  }

  const options: FetchAuditLogsRequest = {
    targetTypes: props.targetTypes,
    sortBy: sortKey,
    limit: itemsPerPage,
    offset: (page - 1) * itemsPerPage,
    actions: ['read','create','update','upsert','delete'],
  }

  if (sortOrder === 'desc') {
    options.desc = true
  }

  if (props.organizationID) {
    options.organizationId = props.organizationID
  }

  if (props.locationID) {
    options.locationId = props.locationID
  }

  if (Array.isArray(props.targetIds) && props.targetIds.length > 0) {
    options.targetIds = props.targetIds
  }

  AuditLog.fetch(options)
    .then(response=> {
      auditLogs.value = response.records.map(auditLog => {
        const transformedAuditLog = props.transformer(auditLog)
        transformedAuditLog.ID = auditLog.ID
        transformedAuditLog.action = auditLog.action
        transformedAuditLog.targetType = auditLog.targetType
        transformedAuditLog.targetID = auditLog.targetID
        transformedAuditLog.userID = auditLog.userID
        transformedAuditLog.username = auditLog.username
        transformedAuditLog.timestamp = auditLog.timestamp
        return transformedAuditLog
      })
      totalItems.value = response.total
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load log entries!')
    })
    .finally(() => {
      loadingEntries.value = false
    })

}

</script>
